import { action, Action, Thunk, thunk } from "easy-peasy";
import { Injections } from "../../../store";
import { StoreModel } from "../..";
import {
  EnumCurrency,
  EnumProductTypology,
  EnumUnit,
  SearchableDateRangeFilterInput,
  SearchableStringFilterInput,
} from "../../../api/graphql/types";
import { IMultiLanguage } from "..";

export interface QuoteItem {
  id: string;
  quantity: number;
  product: {
    id: string;
    name: IMultiLanguage;
    typology: EnumProductTypology;
  };
  booking?: {
    start_date: string;
    end_date: string;
    total_participants: number;
  };
  price_unit: {
    id: string;
    unit: EnumUnit;
  };
  total_price_tax: number;
  total_price: number;
  total_tax: number;
  unit_price: number;
  currency: EnumCurrency;
  is_option: boolean;
}

export interface Quote {
  id: number;
  ref: string;
  status: string;
  disabled: boolean;
  next_status: string[];
  created_at: string;
  expires_at: string;
  total_price: number;
  total_tax: number;
  total_price_tax: number;
  currency: EnumCurrency;
  items: QuoteItem[];
  global_order?: {
    id: string;
  };
  quote_request?: {
    id: number;
  };
  bacK_office_user?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  user: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  can_download_pdf: boolean;
  can_convert_to_order: boolean;
}

export interface GetQuoteOpts {
  id: string;
}

export interface DownloadPdfOpts {
  id: number;
  number: string;
}

export interface UpdateQuoteOpts {
  id: number;
  status: string;
}

export interface DeleteQuoteOpts {
  id: number;
}

export type SearchableQuotesFilterInput = {
  status?: SearchableStringFilterInput;
  lastname?: SearchableStringFilterInput;
  created_at?: SearchableDateRangeFilterInput;
};

export interface ListQuotesOpts {
  buildingId: string;
  limit: number;
  offset: number;
  filter?: SearchableQuotesFilterInput;
}

export interface ConvertToOrderOpts {
  id: number;
}

interface PriceUnit {
  id: string;
}

interface QuoteProduct {
  id: string;
  price_unit: PriceUnit;
  booking?: {
    total_participants: number;
    start_date: string;
    end_date: string;
  };
  quantity?: number;
}

interface QuoteProductWithOptions extends QuoteProduct {
  options?: QuoteProduct[];
}

interface QuoteUser {
  id: string;
}

export interface CreateQuoteOpts {
  user: QuoteUser;
  products: QuoteProductWithOptions[];
  building: {
    id: string;
  };
  quote_request?: {
    id: number;
  };
}

export interface QuoteModel {
  quotes: {
    items: Quote[];
    total: number;
    has_more: boolean;
  };
  quote: Quote | null;

  setQuotes: Action<
    QuoteModel,
    {
      items: Quote[];
      total: number;
      has_more: boolean;
    }
  >;
  setQuote: Action<QuoteModel, Quote | null>;

  listQuotes: Thunk<QuoteModel, ListQuotesOpts, Injections, StoreModel>;
  createQuote: Thunk<QuoteModel, CreateQuoteOpts, Injections, StoreModel>;
  getQuote: Thunk<QuoteModel, GetQuoteOpts, Injections, StoreModel>;
  updateQuote: Thunk<QuoteModel, UpdateQuoteOpts, Injections, StoreModel>;
  downloadPdf: Thunk<QuoteModel, DownloadPdfOpts, Injections, StoreModel>;
  convertToOrder: Thunk<QuoteModel, ConvertToOrderOpts, Injections, StoreModel>;
}

export const quoteModel: QuoteModel = {
  quotes: { items: [], total: 0, has_more: false },
  quote: null,

  setQuotes: action((state, payload) => {
    state.quotes = payload;
  }),
  setQuote: action((state, payload: Quote) => {
    state.quote = payload;
  }),

  createQuote: thunk(
    async (actions, payload: CreateQuoteOpts, { injections }) => {
      const { quoteService } = injections;

      const response = await quoteService.createQuote(payload);
      if (response.status === 201 && response.data.success) {
        actions.setQuote(response.data.data);
      }
    }
  ),
  getQuote: thunk(async (actions, payload: GetQuoteOpts, { injections }) => {
    const { quoteService } = injections;
    const response = await quoteService.getQuote(payload.id);
    if (response.status === 200 && response.data.success) {
      actions.setQuote(response.data.data);
    }
  }),
  updateQuote: thunk(
    async (actions, payload: UpdateQuoteOpts, { injections, getState }) => {
      const { quoteService } = injections;

      const response = await quoteService.updateQuote(payload);

      if (response.status === 200 && response.data.success) {
        const data = response.data.data;
        actions.setQuote(data);

        const state = getState();
        const index = state.quotes.items.findIndex(q => q.id === data.id);

        if (index !== -1) {
          const newItems = [...state.quotes.items];
          newItems.splice(index, 1, data);

          actions.setQuotes({
            ...state.quotes,
            items: newItems,
          });
        }
      }

      return response.data;
    }
  ),
  listQuotes: thunk(
    async (actions, payload: ListQuotesOpts, { injections }) => {
      const { quoteService } = injections;
      const { buildingId, offset, limit, filter } = payload;
      const response = await quoteService.listQuotes(
        buildingId,
        offset,
        limit,
        filter
      );
      if (response.status === 200 && response.data.success) {
        actions.setQuotes(response.data.data);
      }
    }
  ),
  downloadPdf: thunk(
    async (actions, payload: DownloadPdfOpts, { injections }) => {
      const { quoteService } = injections;
      const response = await quoteService.downloadPdf(payload.id);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `quote-${payload.number}.pdf`);

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }
  ),
  convertToOrder: thunk(
    async (actions, payload: ConvertToOrderOpts, { injections }) => {
      const { quoteService } = injections;
      const response = await quoteService.convertToOrder(payload.id);
    }
  ),
};
